/**
 * Pure helpers for manipulating tactic recurrence strings (rrule + EXDATE) used by the
 * recurrence-break feature. Kept free of model imports so `Tactic` can import these without
 * a circular dependency.
 *
 * Recurrence string shape the app uses (single line) plus an optional EXDATE tail:
 *   DTSTART=YYYYMMDDTHHMMSS;FREQ=...;[UNTIL=...Z]\nEXDATE:YYYYMMDDTHHMMSS[,YYYYMMDDTHHMMSS...]
 *
 * Phase 0 spike established that:
 *   - EXDATE must be on its own newline-prefixed line (`\nEXDATE:`), NOT a `;EXDATE=` param.
 *   - The EXDATE datetime must match the occurrence's DTSTART wall-clock digits exactly.
 *   - These date digits are built from LOCAL components (consistent with TacticDetail's
 *     getRecurranceFormattedStringForDate), so parsing inverts with LOCAL components.
 */

/** Format a Date as `YYYYMMDDTHHMMSS` using local components (matches DTSTART formatting). */
export function formatRecurrenceDate(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  )
}

/** Parse a `YYYYMMDDTHHMMSS[Z]` token back into a local Date. Returns null if malformed. */
export function parseRecurrenceDate(token: string): Date | null {
  const match = token.trim().match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?$/)
  if (!match) return null
  const [, year, month, day, hours, minutes, seconds] = match
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds)
  )
}

/** Extract the DTSTART date from a recurrence string's own DTSTART=/DTSTART: token, if present. */
export function getDtStart(rrule: string): Date | null {
  if (!rrule) return null
  const match = rrule.match(/DTSTART[:=]([^;\n]+)/i)
  return match ? parseRecurrenceDate(match[1]) : null
}

/** Return the recurrence string with all EXDATE lines removed (the RRULE body only). */
export function stripExDates(rrule: string): string {
  if (!rrule) return rrule
  return rrule
    .split(/\r?\n/)
    .filter((line) => !/^EXDATE[:=]/i.test(line.trim()))
    .join('\n')
}

/** Extract the raw EXDATE date tokens (e.g. "20260119T080000") from a recurrence string. */
export function getExDateTokens(rrule: string): string[] {
  if (!rrule) return []
  const tokens: string[] = []
  rrule.split(/\r?\n/).forEach((line) => {
    const match = line.trim().match(/^EXDATE[^:=]*[:=](.+)$/i)
    if (!match) return
    match[1]
      .split(',')
      .map((token) => token.trim())
      .filter((token) => token.length > 0)
      .forEach((token) => tokens.push(token))
  })
  return tokens
}

/** Extract EXDATE exception dates from a recurrence string as Dates. */
export function getExDates(rrule: string): Date[] {
  return getExDateTokens(rrule)
    .map((token) => parseRecurrenceDate(token))
    .filter((date): date is Date => date !== null)
}

/**
 * Add an EXDATE exception for `occurrenceDate` to a recurrence string, merging into a single
 * EXDATE line. No-op (returns input) if the date is already excluded or the rrule is empty.
 * The caller must pass an occurrence Date whose wall-clock time matches the series' DTSTART.
 */
export function addExDate(rrule: string, occurrenceDate: Date): string {
  if (!rrule) return rrule
  const token = formatRecurrenceDate(occurrenceDate)
  const existing = getExDateTokens(rrule)
  if (existing.includes(token)) return rrule
  const body = stripExDates(rrule)
  return `${body}\nEXDATE:${[...existing, token].join(',')}`
}

/**
 * Compute the date to assign to a series' `recurrenceEndDate` so that `instanceDate` and every
 * following occurrence are excluded (used by "break this and following" on the original series).
 * Returns the day before the instance at end-of-day; `fromResponseObject` turns this into the
 * `UNTIL=...T235959Z` cap.
 */
export function computeUntilDateForBreak(instanceDate: Date): Date {
  const result = new Date(instanceDate)
  result.setDate(result.getDate() - 1)
  result.setHours(23, 59, 59, 999)
  return result
}

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']

/**
 * Re-anchor an rrule body (no EXDATE) to `newStart`: replace DTSTART and RECOMPUTE the
 * day/date-anchoring BY* values to the new start's weekday/day, preserving their positions.
 * This keeps the pattern following the new start (so a series moved to a different weekday fires
 * on the new weekday) while remaining in the same shape TacticDetail's recurrence dropdown builds
 * (e.g. `FREQ=WEEKLY;WKST=SU;BYDAY=TU`), so the dropdown still displays the selected option.
 */
function reanchorRuleBody(body: string, newStart: Date): string {
  const newDtStart = formatRecurrenceDate(newStart)
  let result = /DTSTART[:=]/i.test(body)
    ? body.replace(/(DTSTART[:=])[^;\n]*/i, `$1${newDtStart}`)
    : `DTSTART=${newDtStart};${body}`

  const weekdayCode = WEEKDAY_CODES[newStart.getDay()]
  // BYDAY: a plain weekday (e.g. TU) -> the new weekday; an ordinal (e.g. 2TU / -1FR, "nth weekday
  // of the month") -> the same ordinal position of the new start's weekday.
  result = result.replace(/(BYDAY=)([^;\n]*)/i, (_match, key: string, value: string) => {
    if (/^-?\d/.test(value)) {
      const nth = Math.ceil(newStart.getDate() / 7)
      return `${key}${nth}${weekdayCode}`
    }
    return `${key}${weekdayCode}`
  })
  // BYMONTHDAY / BYMONTH -> the new start's day-of-month / month.
  result = result.replace(/(BYMONTHDAY=)\d+/i, `$1${newStart.getDate()}`)
  result = result.replace(/(BYMONTH=)\d+/i, `$1${newStart.getMonth() + 1}`)
  return result
}

/**
 * Build the recurrence string for a NEW series that starts at `newStart`: replaces DTSTART with
 * the new start, drops any EXDATE exceptions (a fresh series carries no exceptions), and
 * re-anchors the BY* rules to the new start's weekday/day (see `reanchorRuleBody`).
 * FREQ/INTERVAL/WKST and any existing UNTIL are preserved; the caller copies `recurrenceEndDate`.
 */
export function rebuildRruleWithNewStart(rrule: string, newStart: Date): string {
  return reanchorRuleBody(stripExDates(rrule), newStart)
}

/**
 * Shift an existing series' start from `oldStart` to `newStart`, PRESERVING its EXDATE exceptions
 * by moving each of them by the same delta. Used when applying an edit to a whole recurring series
 * ("All") so already-broken-off instances stay excluded and the exclusion still lines up with the
 * shifted occurrence times. (Unlike `rebuildRruleWithNewStart`, which drops EXDATEs for a fresh
 * series.) BY* anchors are re-anchored to the new start.
 */
export function shiftRruleStart(rrule: string, oldStart: Date, newStart: Date): string {
  const deltaMs = newStart.getTime() - oldStart.getTime()
  const shiftedExDates = getExDates(rrule).map((date) => new Date(date.getTime() + deltaMs))
  const body = reanchorRuleBody(stripExDates(rrule), newStart)
  return shiftedExDates.reduce((acc, date) => addExDate(acc, date), body)
}
